import axios from 'axios';
import RemoveIcon from 'imagesUwKluis/svg/close.svg';
import {cloneDeep} from 'lodash';
import {Fragment, useContext, useEffect, useRef, useState} from 'react';
import useForm from '../../../hooks/useForm';
import {checkAndFormatDates} from '../../../utils/dateHelpers';
import AddItemBox from '../../shared/AddItemBox';
import Button from '../../shared/Button';
import Header from '../../shared/Header';
import {Box, Cluster, Flex, Stack} from '../../shared/layouts';
import {useTheme} from '@emotion/react';
import {useMediaQuery} from '@dnhb/node_package';
import MessagebarContext from '../../../context/MessagebarContext';
import UserContext from '../../../context/UserContext';
import PreventUnload from '../../shared/PreventUnload';
import ConfirmModal from '../../shared/ConfirmModal';
import Loader from '../../shared/Loader';
import WhiteLabelContext from '../../../context/WhiteLabelContext';
import MortgageBlock from './MortgageBlock';
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";
import ReportIncorrectDataLink from "../../shared/ReportIncorrectDataLink";

const MortgagePage = () => {
  // Local state
  const [isLoading, setIsLoading] = useState(true);
  const [mortgages, , setMortgages] = useForm([]);
  const [providers, setProviders] = useState([]);
  const [repaymentTypeOptions, setRepaymentTypeOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    title: '',
    text: '',
    onCancel: () => {},
    onSubmit: () => {},
  });

  // External states
  const mountedRef = useRef(true);
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );
  const { setSuccesMessage, setAlertMessage } = useContext(MessagebarContext);
  const { canEditMortgage, isTenant } = useContext(WhiteLabelContext);
  const { setChangedDataWithoutSaving } = useContext(UserContext);
  const { MortgageIcon } = useIconLibrary();
  const { t } = useTranslation();

  // Local variables
  const hasMortgages = mortgages.length > 0;

  // Effects
  useEffect(() => {
    setChangedDataWithoutSaving(false);
  }, []);

  useEffect(() => {
    axios
      .get('/dossier/mortgage')
      .then(({ data }) => {
        if (!mountedRef.current) return null;
        const mortgage = checkAndFormatDates(data?.mortgage || []);
        const providers = data?.meta?.providers;
        const repaymentTypes = data?.meta?.repaymentTypes;

        setProviders(providers);
        setMortgages(mortgage);
        setRepaymentTypeOptions(repaymentTypes);
      })
      .catch((err) => {
        setAlertMessage();
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Mutations
  const addMortgage = () => {
    const mortgageCopy = cloneDeep(mortgages);
    setMortgages([
      ...mortgageCopy,
      {
        originalAmount: null,
        mortgagenumber: '',
        provider: null,
        label: null,
        loanparts: [],
        registrationAmount: null,
        currentAmount: null,
        ltv: null,
        grossMonthlyPayment: null,
        netMonthlyPayment: null,
      },
    ]);
    setChangedDataWithoutSaving(true);
  };

  const setMortgage = (e, mortgageIndex) => {
    const { name, value } = e.target;
    const mortgageCopy = cloneDeep(mortgages);
    mortgageCopy[mortgageIndex][name] = value;
    setMortgages(mortgageCopy, false);
  };

  const removeMortgage = (mortgageIndex) => {
    setModal({
      isOpen: true,
      title: t('Hypotheek verwijderen'),
      text: t('Weet je zeker dat je deze hypotheek wilt verwijderen? Dit kan niet ongedaan worden gemaakt.'),
      onCancel: () => {
        setModal({ isOpen: false });
      },
      onConfirm: () => {
        const mortgageCopy = cloneDeep(mortgages);
        mortgageCopy.splice(mortgageIndex, 1);
        setMortgages(mortgageCopy, false);
        setModal({ isOpen: false });
        setChangedDataWithoutSaving(true);
        setShowSaveButton(true);
      },
    });
  };

  const addLoanpart = (mortgageIndex, loanpartAmount) => {
    const mortgageCopy = cloneDeep(mortgages);

    mortgageCopy[mortgageIndex].loanparts = [
      ...(mortgageCopy[mortgageIndex].loanparts || []),
      {
        uuid: '',
        loanparttype: '',
        loanpartnumber: '',
        originalAmount: loanpartAmount || null,
        currentAmount: null,
        startDate: '',
        duration: null,
        frtStartDate: '',
        frtEndDate: '',
        frtDuration: null,
        nhg: false,
        ltv: null,
        interest: null,
        interestExpense: null,
        repayment: null,
        premium: null,
        grossMonthlyPayment: null,
      },
    ];
    setMortgages(mortgageCopy, false);
  };

  const setLoanpart = (e, mortgageIndex, loanpartIndex) => {
    const { name, value } = e.target;
    const mortgageCopy = cloneDeep(mortgages);
    mortgageCopy[mortgageIndex].loanparts[loanpartIndex][name] = value;
    setMortgages(mortgageCopy, false);
  };

  const removeLoanpart = (mortgageIndex, loanpartIndex) => {
    setModal({
      isOpen: true,
      title: t('Leningdeel verwijderen'),
      text: t('Weet je zeker dat je dit leningdeel wilt verwijderen? Dit kan niet ongedaan worden gemaakt.'),
      onCancel: () => {
        setModal({ isOpen: false });
      },
      onConfirm: () => {
        const mortgageCopy = cloneDeep(mortgages);
        mortgageCopy[mortgageIndex].loanparts.splice(loanpartIndex, 1);
        setMortgages(mortgageCopy, false);
        setModal({ isOpen: false });
      },
    });
  };

  // Submit
  const saveMortgage = (e) => {
    e.preventDefault();
    setIsLoading(true);
    axios({
      method: 'post',
      url: '/dossier/mortgage',
      data: {
        mortgage: checkAndFormatDates(mortgages, true),
      },
    })
      .then(() => {
        setSuccesMessage();
        setErrors({});
        setChangedDataWithoutSaving(false);
        if (!hasMortgages) setShowSaveButton(false);
      })
      .catch((err) => {
        setAlertMessage();
        const errors = err.response?.data?.errors;
        if (errors) {
          setErrors(() =>
            Object.keys(errors).reduce(
              (acc, val) => ({
                ...acc,
                [val.replace(/^[^.]*[.]/, '')]: errors[val],
              }),
              {}
            )
          );
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Components
  const MortgageHeader = ({ mortgageIndex }) => {
    const isMainHeader = mortgageIndex === 0;
    const showCount = mortgages.length > 1;
    const mortageCount = mortgageIndex + 1;

    return (
      <Header
        title={
          <Cluster hGap="l">
            <span>{t('Hypotheek {{index}}', {index: (showCount ? mortageCount : '')})}</span>
            {hasMortgages && canEditMortgage && (
              <RemoveIcon
                css={(theme) => ({
                  fill: theme?.colors?.alert || 'red',
                  cursor: 'pointer',
                  margin: '4px',
                  width: '32px',
                  height: '32px',
                })}
                onClick={() => removeMortgage(mortgageIndex)}
              />
            )}
          </Cluster>
        }
        icon={<MortgageIcon height="2rem" width="2rem" />}
        variant="page"
        button={
          isMainHeader &&
          hasMortgages &&
          isMinDesktop &&
          canEditMortgage && (
            <Button type="submit">{t('Opslaan')}</Button>
          )
        }
      />
    );
  };

  return (
    <PreventUnload>
      {isLoading && <Loader />}
      <ConfirmModal
        isOpen={modal.isOpen}
        title={modal.title}
        text={modal.text}
        onCancel={modal.onCancel}
        onConfirm={modal.onConfirm}
      />
      <Stack
        as="form"
        onSubmit={saveMortgage}
        space="xl"
        width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}
      >
        <MortgageHeader mortgageIndex={0} />
        {mortgages.map((mortgage, mortgageIndex) => (
          <Fragment key={mortgageIndex}>
            {mortgageIndex > 0 && (
              <MortgageHeader mortgageIndex={mortgageIndex} />
            )}
            <MortgageBlock
              {...{
                mortgage,
                providers,
                errors,
                canEditMortgage,
                setMortgage,
                mortgageIndex,
                addLoanpart,
                setLoanpart,
                removeLoanpart,
                repaymentTypeOptions,
              }}
            />
            {mortgages.length >
              mortgageIndex + !(canEditMortgage && isTenant) && (
              <Box borderTop="default" />
            )}
          </Fragment>
        ))}
        {!canEditMortgage && (
          <ReportIncorrectDataLink />
        )}
        {canEditMortgage && (
          <>
            {(isTenant || !hasMortgages) && (
              <AddItemBox title={t("Hypotheek toevoegen")} onClick={addMortgage} />
            )}
            {(hasMortgages || showSaveButton) && (
              <Flex justifyContent="flex-end">
                <Button type="submit">{t('Opslaan')}</Button>
              </Flex>
            )}
          </>
        )}
      </Stack>
    </PreventUnload>
  );
};

export default MortgagePage;
